/**
 * Shared CSV quantity parsing for inventory-style imports (thousands separators,
 * split unquoted digits, trailing empty columns).
 */

export interface ParseInventoryQuantityOptions {
  allowNegative?: boolean;
}

/** Drop trailing empty/whitespace-only cells. */
export function trimTrailingEmpty(cells: string[]): string[] {
  let end = cells.length;
  while (end > 0 && cells[end - 1].trim() === "") {
    end -= 1;
  }
  return cells.slice(0, end);
}

function firstSegmentMatchesThousandsPrefix(part: string, allowNegative: boolean): boolean {
  const t = part.replace(/,/g, "").trim();
  if (allowNegative) {
    return /^-?\d{1,3}$/.test(t);
  }
  return /^\d{1,3}$/.test(t);
}

/**
 * When a row has more cells than the header (unquoted thousands like `1,234`),
 * merge overflow segments into the quantity column when they look like grouped digits.
 */
export function reconcileQuantityOverflow(
  values: string[],
  headerLength: number,
  quantityIndex: number,
  opts?: ParseInventoryQuantityOptions,
): string[] {
  const allowNegative = opts?.allowNegative === true;
  const overflowColumns = values.length - headerLength;
  if (overflowColumns <= 0) {
    return [...values];
  }
  const adjustedValues = [...values];
  const mergeEndIndex = quantityIndex + overflowColumns;
  const candidateParts = adjustedValues
    .slice(quantityIndex, mergeEndIndex + 1)
    .map((value) => value.trim())
    .filter(Boolean);
  const canMergeOverflowIntoQuantity =
    candidateParts.length === overflowColumns + 1 &&
    firstSegmentMatchesThousandsPrefix(candidateParts[0], allowNegative) &&
    candidateParts.slice(1).every((segment) => /^\d{3}$/.test(segment));
  if (canMergeOverflowIntoQuantity) {
    adjustedValues.splice(quantityIndex, overflowColumns + 1, candidateParts.join(","));
  }
  return adjustedValues;
}

/**
 * If quantity is the last logical column, join following `ddd` segments as thousands groups.
 */
export function mergeThousandsIfLastColumn(
  trimmedValues: string[],
  quantityIndex: number,
  headerLength: number,
  opts?: ParseInventoryQuantityOptions,
): string {
  const allowNegative = opts?.allowNegative === true;
  let quantityRaw = trimmedValues[quantityIndex] ?? "";
  const trailingQuantityParts = trimmedValues
    .slice(quantityIndex + 1)
    .map((value) => value.trim())
    .filter(Boolean);
  const canMergeThousandsSegments =
    quantityIndex === headerLength - 1 &&
    trailingQuantityParts.length > 0 &&
    firstSegmentMatchesThousandsPrefix(quantityRaw, allowNegative) &&
    trailingQuantityParts.every((segment) => /^\d{3}$/.test(segment));
  if (canMergeThousandsSegments) {
    quantityRaw = [quantityRaw, ...trailingQuantityParts].join(",");
  }
  return quantityRaw;
}

export function parseInventoryQuantity(
  raw: string,
  opts?: ParseInventoryQuantityOptions,
): number | null {
  const normalized = raw.replace(/,/g, "").trim();
  if (!normalized) return null;
  const allowNegative = opts?.allowNegative === true;
  const pattern = allowNegative ? /^-?\d+$/ : /^\d+$/;
  if (!pattern.test(normalized)) return null;
  const quantity = Number(normalized);
  if (!Number.isFinite(quantity)) return null;
  if (!allowNegative && quantity < 0) return null;
  return quantity;
}

/** Reconcile split thousands overflow and trim trailing empty cells (one pass per row). */
export function normalizeInventoryCsvRow(
  values: string[],
  headerLength: number,
  quantityIndex: number,
  opts?: ParseInventoryQuantityOptions,
): string[] {
  return trimTrailingEmpty(reconcileQuantityOverflow(values, headerLength, quantityIndex, opts));
}

/** Reconcile overflow, trim trailing empties, merge last-column thousands, parse. */
export function parseInventoryRowQuantity(
  values: string[],
  headerLength: number,
  quantityIndex: number,
  opts?: ParseInventoryQuantityOptions,
): number | null {
  const row = normalizeInventoryCsvRow(values, headerLength, quantityIndex, opts);
  const raw = mergeThousandsIfLastColumn(row, quantityIndex, headerLength, opts);
  return parseInventoryQuantity(raw, opts);
}
